import { v4 as uuidv4 } from 'uuid';
import type { FeaturePermissions, ModuleTemplate, SectionItem, TemplateProperty } from '../types/modules';
import { ModuleTemplateSettings } from '../types/modules';

export function createSection(symbol: string = null, name: string)
{
    const id = uuidv4();

    const section: SectionItem = {
        id: id,
        symbol: symbol || '',
        name: name,
        displayName: {},
        formId: null,
        template: 'Table',
        isHidden: false,
        columns: {
            enabled: undefined,
            visible: {},
            positions: {}
        },
        filters: {
            enabled: undefined,
            visible: {},
            positions: {}
        },
        permissions: [
            {
                items: [
                    { value: '', name: '', scope: 'All' }
                ]
            }
        ],
        features: {
            access: { permissions: [], condition: 'And', type: 'Access' },
            create: { permissions: [], condition: 'And', type: 'Access' },
            view: { permissions: [], condition: 'And', type: 'All' },
            update: { permissions: [], condition: 'And', type: 'All' },
            delete: { permissions: [], condition: 'And', type: 'All' },
            accept: { permissions: [], condition: 'And', type: 'All' },
            manage: { permissions: [], condition: 'And', type: 'All' },
            export: { permissions: [], condition: 'And', type: 'Access' }
        },
        settings: {
            canAdd: true,
            canExport: false,
            editOnModal: false,
            modalSize: 'md',
            scrollableModal: false,
            hasResourcePermissions: false,
            enableFilters: true,
            customisableColumns: true
        }
    };

    simplePermissions(section);

    return section;
}

export function setAccess(feature: FeaturePermissions, permissions: string[], condition?: 'And' | 'Or')
{
    feature.permissions = permissions;

    if (condition)
        feature.condition = condition;
}

export function simplePermissions(section: SectionItem)
{
    section.permissions = [
        {
            items: [
                { value: 'Access', name: $t('[[[Dostęp do modułu]]]'), scope: 'All' }
            ]
        }
    ];

    setAccess(section.features.access, ['Access'], 'And');
    setAccess(section.features.create, ['Access'], 'And');
    setAccess(section.features.view, ['Access'], 'And');
    setAccess(section.features.update, ['Access'], 'And');
    setAccess(section.features.delete, ['Access'], 'And');
    setAccess(section.features.accept, ['Access'], 'And');
    setAccess(section.features.manage, ['Access'], 'And');
    setAccess(section.features.export, ['Access'], 'And');
}

export function standardPermissions(section: SectionItem)
{
    section.permissions = [
        {
            items: [
                { value: 'Access', name: $t('[[[Dostęp do modułu]]]'), scope: 'All' }
            ]
        },
        {
            items: [
                { value: 'Edit', name: $t('[[[Edycja dokumentów]]]'), scope: 'All' },
                { value: 'Delete', name: $t('[[[Usuwanie dokumentów]]]'), scope: 'All' },
                { value: 'Accept', name: $t('[[[Akceptacja dokumentów]]]'), scope: 'All' },
                { value: 'Manage', name: $t('[[[Nadawanie uprawnień do dokumentów]]]'), scope: 'All' }
            ]
        }
    ];

    setAccess(section.features.access, ['Access'], 'And');
    setAccess(section.features.create, ['Edit'], 'And');
    setAccess(section.features.view, ['Edit'], 'And');
    setAccess(section.features.update, ['Edit'], 'And');
    setAccess(section.features.delete, ['Delete'], 'And');
    setAccess(section.features.accept, ['Accept'], 'And');
    setAccess(section.features.manage, ['Manage'], 'And');
    setAccess(section.features.export, ['Edit'], 'And');
}

export function advancedPermissions(section: SectionItem)
{
    section.permissions = [
        {
            items: [
                { value: 'Access', name: $t('[[[Dostęp do modułu]]]'), scope: 'All' }
            ]
        },
        {
            items: [
                { value: 'Edit', name: $t('[[[Dodawanie i edycja własnych dokumentów]]]'), scope: 'Mine' },
                { value: 'Delete', name: $t('[[[Usuwanie własnych dokumentów]]]'), scope: 'Mine' },
                { value: 'Accept', name: $t('[[[Akceptacja własnych dokumentów]]]'), scope: 'Mine' },
                { value: 'Manage', name: $t('[[[Nadawanie uprawnień do własnych dokumentów]]]'), scope: 'Mine' }
            ]
        },
        {
            items: [
                { value: 'EditAll', name: $t('[[[Edycja wszystkich dokumentów]]]'), scope: 'All' },
                { value: 'DeleteAll', name: $t('[[[Usuwanie wszystkich dokumentów]]]'), scope: 'All' },
                { value: 'AcceptAll', name: $t('[[[Akceptacja wszystkich dokumentów]]]'), scope: 'All' },
                { value: 'ManageAll', name: $t('[[[Nadawanie uprawnień do wszystkich dokumentów]]]'), scope: 'All' }
            ]
        },
        {
            items: [
                { value: 'EditThis', name: $t('[[[Edycja dokumentu]]]'), scope: 'Selected' },
                { value: 'DeleteThis', name: $t('[[[Usunięcie dokumentu]]]'), scope: 'Selected' },
                { value: 'AcceptThis', name: $t('[[[Akceptacja dokumentu]]]'), scope: 'Selected' }
            ]
        }
    ];

    setAccess(section.features.access, ['Access'], 'And');
    setAccess(section.features.create, ['Edit', 'EditAll'], 'Or');
    setAccess(section.features.view, ['Edit', 'EditAll', 'EditThis'], 'Or');
    setAccess(section.features.update, ['Edit', 'EditAll', 'EditThis'], 'Or');
    setAccess(section.features.delete, ['Delete', 'DeleteAll', 'DeleteThis'], 'Or');
    setAccess(section.features.accept, ['Accept', 'AcceptAll', 'AcceptThis'], 'Or');
    setAccess(section.features.manage, ['Manage', 'ManageAll'], 'Or');
    setAccess(section.features.export, ['EditAll'], 'Or');
}

export function resetPermissions(section: SectionItem)
{
    section.permissions = [
        {
            items: [
                { value: '', name: '', scope: 'All' }
            ]
        }
    ];
}

export function has(template: ModuleTemplate, feature: TemplateProperty = null)
{
    if (feature)
        return ModuleTemplateSettings[template].includes(feature);
    else
        return ModuleTemplateSettings[template].any();
}

export function checkPermissions(permissions: Record<string, boolean>, feature: FeaturePermissions)
{
    const allowed = Object
        .entries(permissions)
        .where(([_, value]) => value == true)
        .select(([key, _]) => key)
        .toArray();

    // Jak nie wymagamy jakichkolwiek uprawnień to znaczy, ze chcemy wpuścić
    if (feature.permissions.length == 0)
    {
        return true;
    }

    if (feature.condition == 'And')
    {
        for (let i = 0; i < feature.permissions.length; i++)
        {
            if (!allowed.includes(feature.permissions[i]))
                return false;
        }

        return true;
    }
    else
    {
        for (let i = 0; i < feature.permissions.length; i++)
        {
            if (allowed.includes(feature.permissions[i]))
                return true;
        }

        return false;
    }
}

export function translate(value: any)
{
    if (typeof value == 'string')
        return value as string;

    if (value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.keys(value).every(key => typeof value[key] === 'string'))
    {
        return useLocalization().$i18n.currentTranslation(value);
    }

    return '';
}
